import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  roles?: Array<{ name: string }>;
  tenant?: string;
  userType?: 'admin' | 'customer' | 'partner';  // Add userType field
  partnerId?: string;  // Add partnerId field for partner users
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<any>;
  completeLogin: (completionToken: string, totpCode?: string) => Promise<any>;
  migratePassword: (resetToken: string, newPassword: string) => Promise<any>;
  loginWithSSO: (token: string, userData: any) => void;
  logout: () => void;
  isPartner: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPartner, setIsPartner] = useState(false);

  const normalizeUser = (rawUser: any): User => {
    if (!rawUser) {
      throw new Error('Cannot normalise empty user object');
    }

    const userId = rawUser.id || rawUser._id || rawUser.userId;
    if (!userId) {
      throw new Error('Unable to determine user id when normalising user');
    }

    const partnerId = (rawUser.partnerId && typeof rawUser.partnerId === 'object')
      ? rawUser.partnerId._id || rawUser.partnerId.$oid || rawUser.partnerId.toString?.()
      : rawUser.partnerId || rawUser.partner?._id;

    const roleName = rawUser.role?.name || rawUser.role;
    const rolesArray: Array<{ name: string }> | undefined = rawUser.roles;

    const resolvedUserType = (rawUser.userType as User['userType'])
      || (typeof roleName === 'string' && roleName.toLowerCase() === 'partner' ? 'partner' : undefined)
      || (rolesArray?.some((role) => role.name?.toLowerCase() === 'partner') ? 'partner' : undefined)
      || (partnerId ? 'partner' : undefined)
      || (typeof roleName === 'string' && roleName.toLowerCase() === 'admin' ? 'admin' : undefined)
      || 'customer';

    const [derivedFirstName = '', ...restName] = (rawUser.name || '').trim().split(/	+|\s+/);
    const derivedLastName = restName.length ? restName.join(' ') : '';

    return {
      id: userId,
      email: rawUser.email,
      firstName: rawUser.firstName ?? derivedFirstName,
      lastName: rawUser.lastName ?? derivedLastName,
      role: typeof roleName === 'string' ? roleName : undefined,
      roles: rolesArray,
      tenant: rawUser.tenant,
      userType: resolvedUserType,
      partnerId: partnerId || undefined,
    };
  };

  useEffect(() => {
    // Check for existing token
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser && storedUser !== 'undefined') {
      try {
        const parsedUser = JSON.parse(storedUser);
        const normalizedUser = normalizeUser(parsedUser);
        setToken(storedToken);
        setUser(normalizedUser);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        if (!localStorage.getItem('selectedTenantId') && normalizedUser.tenant) {
          localStorage.setItem('selectedTenantId', normalizedUser.tenant);
        }
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        
        // Check if user is a partner
        checkPartnerRole(normalizedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  const checkPartnerRole = (user: User) => {
    // Check multiple ways to identify a partner user
    const hasPartnerRole =
      user.userType === 'partner' ||  // Check userType first (most reliable)
      user.role === 'partner' ||
      user.roles?.some(role => role.name?.toLowerCase() === 'partner') ||
      Boolean(user.partnerId) ||
      false;
    setIsPartner(hasPartnerRole);
    console.log('[AuthContext] Partner check:', {
      userType: user.userType,
      role: user.role,
      isPartner: hasPartnerRole
    });
  };

  const login = async (email: string, password: string, totpCode?: string) => {
    try {
      const loginData: any = {
        email,
        password,
      };

      // Only include totpCode if it's provided and not empty
      if (totpCode && totpCode.trim()) {
        loginData.totpCode = totpCode;
      }

      const response = await axios.post(`${API_URL}/auth/login`, loginData);

      const data = response.data;

      // Check if password update is required
      if (data.requiresPasswordUpdate) {
        console.log('[Partner Portal] Password update required');
        return {
          requiresPasswordUpdate: true,
          resetToken: data.resetToken,
          user: data.user,
        };
      }

      // Check if 2FA is required
      if (data.requires2FA) {
        console.log('[Partner Portal] 2FA required');
        return { requires2FA: true };
      }

      // Check if 2FA setup is required
      if (data.requiresSetup) {
        console.log('[Partner Portal] 2FA setup required');
        return { requiresSetup: true, tempToken: data.tempToken };
      }

      // Extract token - use resetToken if password update required, otherwise use normal token
      const token = data.token || data.resetToken;
      const user = data.user;

      console.log('[AuthContext] Raw response data from backend:', JSON.stringify(data, null, 2));
      console.log('[AuthContext] User object from backend:', user);
      console.log('[AuthContext] user.userType from backend:', user?.userType);
      console.log('[AuthContext] user.partnerId from backend:', user?.partnerId);
      console.log('[AuthContext] Extracted token:', token ? 'Present' : 'Missing');

      if (!user || !token) {
        throw new Error('Invalid response from server - missing user or token');
      }

      const normalizedUser = normalizeUser(user);
      console.log('[AuthContext] Normalized user:', normalizedUser);
      console.log('[AuthContext] Normalized userType:', normalizedUser.userType);

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      localStorage.setItem('fromPartnerPortal', 'true');

      // Always update selectedTenantId on fresh login — a stale value from a
      // previous session (e.g. a different tenant) must be overwritten so that
      // X-Tenant-Id points to the tenant where this user's CRM data lives.
      if (normalizedUser.tenant) {
        localStorage.setItem('selectedTenantId', normalizedUser.tenant);
      }

      // Set state
      setToken(token);
      setUser(normalizedUser);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Check partner role
      checkPartnerRole(normalizedUser);

      // Return the full result including user data
      return { success: true, user: normalizedUser };
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const completeLogin = async (completionToken: string, totpCode?: string) => {
    try {
      const loginData: any = { completionToken };

      if (totpCode && totpCode.trim()) {
        loginData.totpCode = totpCode;
      }

      const response = await axios.post(`${API_URL}/auth/complete-login`, loginData);
      const data = response.data;

      // Check if 2FA is still required
      if (data.requires2FA) {
        console.log('[Partner Portal] 2FA still required');
        return { requires2FA: true };
      }

      // Check if 2FA setup is required
      if (data.requiresSetup) {
        console.log('[Partner Portal] 2FA setup required');
        return { requiresSetup: true, tempToken: data.tempToken };
      }

      const token = data.token;
      const user = data.user;

      if (!user || !token) {
        throw new Error('Invalid response from server');
      }

      const normalizedUser = normalizeUser(user);

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      localStorage.setItem('fromPartnerPortal', 'true');

      // Always update selectedTenantId on fresh login (see login() comment above)
      if (normalizedUser.tenant) {
        localStorage.setItem('selectedTenantId', normalizedUser.tenant);
      }

      // Set state
      setToken(token);
      setUser(normalizedUser);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Check partner role
      checkPartnerRole(normalizedUser);

      return { success: true, user: normalizedUser };
    } catch (error: any) {
      console.error('Complete login error:', error);
      throw error;
    }
  };

  const migratePassword = async (resetToken: string, newPassword: string) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/password/migrate`,
        {
          resetToken,  // Backend expects resetToken in body
          newPassword,
        }
      );

      const data = response.data;

      if (data.passwordUpdated) {
        console.log('[Partner Portal] Password updated successfully');
        return {
          passwordUpdated: true,
          requires2FA: data.requires2FA,
          requiresSetup: data.requiresSetup,
          completionToken: data.completionToken,
        };
      }

      return data;
    } catch (error: any) {
      console.error('Password migration error:', error);
      throw error;
    }
  };

  const loginWithSSO = (ssoToken: string, userData: any) => {
    const normalizedUser = normalizeUser(userData);
    localStorage.setItem('token', ssoToken);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    localStorage.setItem('fromPartnerPortal', 'true');
    if (normalizedUser.tenant) {
      localStorage.setItem('selectedTenantId', normalizedUser.tenant);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${ssoToken}`;
    setToken(ssoToken);
    setUser(normalizedUser);
    checkPartnerRole(normalizedUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('fromPartnerPortal');
    localStorage.removeItem('selectedTenantId'); // Clear tenant ID on logout
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsPartner(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, completeLogin, migratePassword, loginWithSSO, logout, isPartner }}>
      {children}
    </AuthContext.Provider>
  );
};
